import { useEffect, useRef, useState } from "react";
import { EventLayout } from "@/components/EventLayout";
import { useEvent } from "@/context/EventContext";
import { Gate } from "@/components/FeatureGate";
import { photoFileUrl } from "@/lib/api";
import { useInfiniteWindow } from "@/lib/useInfiniteWindow";
import { toast } from "sonner";
import { Upload, Check, Trash2, Eye, EyeOff, X, Download, FolderPlus, Ban, FolderInput, CheckSquare, Square } from "lucide-react";

export default function Gallery() {
  const { role } = useEvent();
  return role === "attendee" ? <AttendeeGallery /> : <HostGallery />;
}

function HostGallery() {
  const { eapi } = useEvent();
  return (
    <EventLayout>
      <Gate featureKey="gallery">
        <HostGalleryInner eapi={eapi} />
      </Gate>
    </EventLayout>
  );
}

function HostGalleryInner({ eapi }) {
  const [photos, setPhotos] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [newAlbum, setNewAlbum] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = () => eapi.get("/photos").then(({ data }) => setPhotos(data)).catch(() => {});
  const loadAlbums = () => eapi.get("/albums").then(({ data }) => setAlbums(data.albums || [])).catch(() => {});
  useEffect(() => { load(); loadAlbums(); /* eslint-disable-next-line */ }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) { const fd = new FormData(); fd.append("file", f); await eapi.post("/photos/upload", fd); }
      toast.success(`Uploaded ${files.length} photo${files.length > 1 ? "s" : ""}`); load();
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const blobDownload = (data, name) => {
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const downloadAll = async () => {
    setDownloading(true);
    try { const res = await eapi.get("/gallery/download", { responseType: "blob" }); blobDownload(res.data, "gallery.zip"); }
    catch (e) { toast.error(e.response?.status === 404 ? "There are no photos to download yet." : "Could not prepare the download."); }
    finally { setDownloading(false); }
  };
  const approve = async (id) => { await eapi.put(`/photos/${id}/approve`); load(); };
  const toggleVis = async (p) => { await eapi.put(`/photos/${p.id}/visibility`, { visibility: p.visibility === "private" ? "guests" : "private" }); load(); };
  const remove = async (id) => { await eapi.delete(`/photos/${id}`); load(); toast.success("Photo removed"); };

  const addAlbum = async () => {
    const n = newAlbum.trim(); if (!n) return;
    try { const { data } = await eapi.post("/albums", { name: n }); setAlbums(data.albums || []); setNewAlbum(""); toast.success(`Album "${n}" created`); }
    catch (e) { toast.error("Could not create the album."); }
  };
  const removeAlbum = async (name) => {
    try { const { data } = await eapi.delete(`/albums/${encodeURIComponent(name)}`); setAlbums(data.albums || []); load(); toast.success(`Album "${name}" removed`); }
    catch (e) { toast.error("Could not remove the album."); }
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => { setSelected(new Set()); setSelectMode(false); };
  const bulk = async (action, album) => {
    if (!selected.size) return;
    setBusy(true);
    try {
      const { data } = await eapi.post("/photos/bulk", { action, photo_ids: [...selected], album: album || null });
      const labels = { approve: "approved", reject: "rejected", delete: "deleted", private: "made private", share: "shared", move: "moved" };
      toast.success(`${data.affected} photo(s) ${labels[action] || "updated"}`);
      clearSel(); load();
    } catch (e) { toast.error("That bulk action didn't work."); }
    finally { setBusy(false); }
  };
  const bulkDownload = async () => {
    if (!selected.size) return;
    setBusy(true);
    try { const res = await eapi.get(`/gallery/download?ids=${[...selected].join(",")}`, { responseType: "blob" }); blobDownload(res.data, "photos.zip"); }
    catch (e) { toast.error("Could not prepare the download."); }
    finally { setBusy(false); }
  };

  const pending = photos.filter((p) => !p.is_approved);
  const approved = photos.filter((p) => p.is_approved);
  const orderedApproved = [...albums.flatMap((a) => approved.filter((p) => p.album === a)), ...approved.filter((p) => !p.album)];
  const { count, sentinelRef, hasMore } = useInfiniteWindow(orderedApproved.length, 48);
  const visible = orderedApproved.slice(0, count);
  const ungroupedVisible = visible.filter((p) => !p.album);

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 lg:py-12">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div><p className="overline">Memories</p><h1 className="font-serif text-4xl tracking-tight">Photo gallery</h1></div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setSelectMode((v) => !v); setSelected(new Set()); }} data-testid="select-mode-btn"
            className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm transition-colors ${selectMode ? "border-[#8C9A86] bg-[#8C9A86]/10 text-[#5f6b59]" : "border-[#E8E6E1] text-stone-600 hover:bg-stone-50"}`}>
            <CheckSquare size={15} /> {selectMode ? "Done selecting" : "Select photos"}
          </button>
          {approved.length > 0 && (
            <button onClick={downloadAll} disabled={downloading} data-testid="download-all-btn" className="flex items-center gap-2 rounded-full border border-[#E8E6E1] text-stone-600 px-5 py-2.5 text-sm hover:bg-stone-50 transition-colors disabled:opacity-50"><Download size={15} /> {downloading ? "Preparing…" : "Download all"}</button>
          )}
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-btn" className="flex items-center gap-2 rounded-full bg-[#2C2A28] text-white px-5 py-2.5 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-50"><Upload size={15} /> {uploading ? "Uploading…" : "Upload photos"}</button>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload} data-testid="file-input" />
        </div>
      </div>

      {/* Albums manager */}
      <div className="mb-8 bg-white border border-[#E8E6E1] rounded-lg p-5" data-testid="albums-manager">
        <div className="flex items-center justify-between mb-3"><p className="text-sm font-medium text-stone-700">Albums</p></div>
        <div className="flex flex-wrap items-center gap-2">
          {albums.map((a) => (
            <span key={a} data-testid={`album-chip-${a}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#E8E6E1] bg-[#FDFBF7] pl-3 pr-1.5 py-1.5 text-xs text-stone-600">
              {a}
              <button onClick={() => removeAlbum(a)} data-testid={`album-del-${a}`} title="Delete album" className="text-stone-300 hover:text-[#C67A7A]"><X size={12} /></button>
            </span>
          ))}
          {albums.length === 0 && <span className="text-xs text-stone-400">No albums yet. Group photos into named albums like "Ceremony" or "Reception".</span>}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <input value={newAlbum} onChange={(e) => setNewAlbum(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addAlbum()} placeholder="New album name" data-testid="new-album-input"
            className="flex-1 max-w-xs rounded-full border border-[#E8E6E1] bg-white px-4 py-2 text-sm outline-none focus:border-[#8C9A86]" />
          <button onClick={addAlbum} data-testid="add-album-btn" className="flex items-center gap-1.5 rounded-full border border-[#8C9A86] text-[#5f6b59] px-4 py-2 text-sm hover:bg-[#8C9A86]/10 transition-colors"><FolderPlus size={14} /> Create album</button>
        </div>
      </div>

      {pending.length > 0 && (
        <div className="mb-10">
          <p className="overline mb-3">Awaiting your approval ({pending.length})</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {pending.map((p) => (
              <HostPhoto key={p.id} p={p} pending selectMode={selectMode} selected={selected.has(p.id)} onToggle={toggleSel}
                onApprove={approve} onRemove={remove} onVis={toggleVis} onOpen={setLightbox} />
            ))}
          </div>
        </div>
      )}

      {approved.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E8E6E1] rounded-lg p-16 text-center text-sm text-stone-400">No photos yet. Upload your first memories.</div>
      ) : (
        <>
          {albums.map((a) => {
            const ph = visible.filter((p) => p.album === a);
            if (!ph.length) return null;
            return (
              <div key={a} className="mb-8" data-testid={`album-section-${a}`}>
                <p className="overline mb-3">{a} ({approved.filter((p) => p.album === a).length})</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {ph.map((p) => (
                    <HostPhoto key={p.id} p={p} selectMode={selectMode} selected={selected.has(p.id)} onToggle={toggleSel}
                      onApprove={approve} onRemove={remove} onVis={toggleVis} onOpen={setLightbox} />
                  ))}
                </div>
              </div>
            );
          })}
          {ungroupedVisible.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {ungroupedVisible.map((p) => (
                <HostPhoto key={p.id} p={p} selectMode={selectMode} selected={selected.has(p.id)} onToggle={toggleSel}
                  onApprove={approve} onRemove={remove} onVis={toggleVis} onOpen={setLightbox} />
              ))}
            </div>
          )}
          {hasMore && <div ref={sentinelRef} data-testid="gallery-sentinel" className="h-10" />}
        </>
      )}
      <p className="text-xs text-stone-400 mt-6">Tip: "Shared" photos appear on your public page and in your guests' memory galleries forever. "Private" photos are only visible to you. Only you see who uploaded each photo.</p>

      {selectMode && selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#2C2A28] text-white rounded-2xl shadow-lg flex items-center gap-2 px-4 py-3 fade-up flex-wrap justify-center max-w-[95vw]" data-testid="gallery-bulk-bar">
          <span className="text-sm mr-1">{selected.size} selected</span>
          <button onClick={() => bulk("approve")} disabled={busy} data-testid="bulk-accept-btn" className="flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm"><Check size={14} /> Accept all</button>
          <button onClick={() => bulk("reject")} disabled={busy} data-testid="bulk-reject-btn" className="flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm"><Ban size={14} /> Reject all</button>
          <button onClick={() => bulk("delete")} disabled={busy} data-testid="bulk-delete-btn" className="flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm"><Trash2 size={14} /> Delete all</button>
          <button onClick={() => bulk("private")} disabled={busy} data-testid="bulk-private-btn" className="flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm"><EyeOff size={14} /> Make private</button>
          <button onClick={bulkDownload} disabled={busy} data-testid="bulk-download-btn" className="flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm"><Download size={14} /> Download all</button>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 pl-3 pr-1.5 py-1 text-sm">
            <FolderInput size={14} />
            <select onChange={(e) => { if (e.target.value) bulk("move", e.target.value === "__none__" ? null : e.target.value); e.target.value = ""; }} defaultValue="" data-testid="bulk-move-select"
              className="bg-transparent text-white text-sm outline-none [&>option]:text-stone-800">
              <option value="" disabled>Move to album…</option>
              {albums.map((a) => <option key={a} value={a}>{a}</option>)}
              <option value="__none__">Remove from album</option>
            </select>
          </span>
          <button onClick={clearSel} data-testid="bulk-clear-btn" className="p-1.5 text-white/70 hover:text-white"><X size={16} /></button>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 grid place-items-center p-6" onClick={() => setLightbox(null)}>
          <button className="absolute top-6 right-6 text-white/70 hover:text-white"><X size={26} /></button>
          <img src={photoFileUrl(lightbox.id)} alt="" className="max-h-[85vh] max-w-full rounded-lg object-contain" />
        </div>
      )}
    </div>
  );
}

function HostPhoto({ p, pending, selectMode, selected, onToggle, onApprove, onRemove, onVis, onOpen }) {
  return (
    <div className={`relative rounded-lg overflow-hidden group ${pending ? "border border-[#D4C5B3]" : ""} ${selected ? "ring-2 ring-[#8C9A86] ring-offset-2" : ""}`} data-testid={pending ? `pending-photo-${p.id}` : `photo-${p.id}`}>
      <img src={photoFileUrl(p.id, { thumb: true })} alt="" loading="lazy" className="w-full h-44 object-cover cursor-pointer" onClick={() => (selectMode ? onToggle(p.id) : onOpen(p))} />
      {selectMode && (
        <button onClick={() => onToggle(p.id)} data-testid={`select-photo-${p.id}`} className="absolute top-2 right-2 text-white bg-black/40 rounded-md p-1 pointer-events-auto">
          {selected ? <CheckSquare size={18} /> : <Square size={18} />}
        </button>
      )}
      {!pending && (
        <span className={`absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full ${p.visibility === "private" ? "bg-[#2C2A28] text-white" : "bg-white/90 text-[#5f6b59]"}`}>{p.visibility === "private" ? "Private" : "Shared"}</span>
      )}
      {!selectMode && (
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 pointer-events-none">
          {pending ? (
            <>
              <button onClick={() => onApprove(p.id)} data-testid={`approve-${p.id}`} className="rounded-full bg-white text-[#8C9A86] p-2 pointer-events-auto"><Check size={16} /></button>
              <button onClick={() => onRemove(p.id)} className="rounded-full bg-white text-[#C67A7A] p-2 pointer-events-auto"><Trash2 size={16} /></button>
            </>
          ) : (
            <>
              <button onClick={() => onVis(p)} data-testid={`vis-${p.id}`} title={p.visibility === "private" ? "Make visible to guests" : "Make private"} className="rounded-full bg-white text-stone-700 p-2 pointer-events-auto">{p.visibility === "private" ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              <button onClick={() => onRemove(p.id)} data-testid={`del-${p.id}`} className="rounded-full bg-white text-[#C67A7A] p-2 pointer-events-auto"><Trash2 size={16} /></button>
            </>
          )}
        </div>
      )}
      {p.uploaded_by && <span className="absolute bottom-1 left-1 text-[10px] text-white bg-black/50 px-1.5 py-0.5 rounded" data-testid={`uploader-${p.id}`}>by {p.uploaded_by}</span>}
    </div>
  );
}

function AttendeeGallery() {
  const { eapi, event } = useEvent();
  const [photos, setPhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [albumFilter, setAlbumFilter] = useState("all");
  const fileRef = useRef();
  const load = () => eapi.get("/gallery").then(({ data }) => setPhotos(data.photos)).catch(() => setPhotos([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eapi]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) { const fd = new FormData(); fd.append("file", f); await eapi.post("/gallery/upload", fd); }
      toast.success("Thanks! Your photos are awaiting the host's approval.");
      load();
    } catch (err) { toast.error(err.response?.status === 403 ? "Photo sharing isn't open for this event yet." : "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const pendingMine = photos.filter((p) => !p.is_approved);
  const shared = photos.filter((p) => p.is_approved);
  const albumList = [...new Set(shared.map((p) => p.album).filter(Boolean))].sort();
  const ordered = [...albumList.flatMap((a) => shared.filter((p) => p.album === a)), ...shared.filter((p) => !p.album)];
  const shownList = albumFilter === "all" ? ordered : shared.filter((p) => (p.album || "") === albumFilter);
  const { count, sentinelRef, hasMore } = useInfiniteWindow(shownList.length, 48);
  const visible = shownList.slice(0, count);

  const grid = (list) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      {list.map((p) => <img key={p.id} src={photoFileUrl(p.id, { thumb: true })} alt="" loading="lazy" data-testid={`photo-${p.id}`} className="w-full h-44 object-cover rounded-lg cursor-pointer hover:opacity-90" onClick={() => setLightbox(p)} />)}
    </div>
  );

  return (
    <EventLayout>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 lg:py-12" data-testid="attendee-gallery">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <p className="overline">Memories</p>
            <h1 className="font-serif text-4xl tracking-tight" style={{ fontFamily: event?.theme_font || undefined }}>{event?.title}</h1>
          </div>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="attendee-upload-btn" className="flex items-center gap-2 rounded-full bg-[#2C2A28] text-white px-5 py-2.5 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-50"><Upload size={15} /> {uploading ? "Uploading…" : "Add your photos"}</button>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload} data-testid="attendee-file-input" />
        </div>
        {pendingMine.length > 0 && (
          <div className="mb-10" data-testid="my-pending-section">
            <p className="overline mb-3">Your photos · awaiting the host's approval ({pendingMine.length})</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {pendingMine.map((p) => (
                <div key={p.id} className="relative rounded-lg overflow-hidden border border-[#D4C5B3]" data-testid={`my-pending-${p.id}`}>
                  <img src={photoFileUrl(p.id, { thumb: true })} alt="" loading="lazy" className="w-full h-44 object-cover cursor-pointer" onClick={() => setLightbox(p)} />
                  <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-[#2C2A28]/80 text-white">Awaiting approval</span>
                </div>))}
            </div>
          </div>
        )}
        {albumList.length > 0 && (
          <div className="flex justify-end mb-5">
            <select value={albumFilter} onChange={(e) => setAlbumFilter(e.target.value)} data-testid="attendee-album-filter"
              className="rounded-full border border-[#E8E6E1] bg-white px-4 py-2 text-sm outline-none focus:border-[#8C9A86]">
              <option value="all">All photos</option>
              {albumList.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        )}
        {shared.length === 0 ? (
          <div className="bg-white border border-dashed border-[#E8E6E1] rounded-lg p-16 text-center text-sm text-stone-400">No shared photos yet. Check back later!</div>
        ) : albumFilter === "all" ? (
          <>
            {albumList.map((a) => {
              const ph = visible.filter((p) => p.album === a);
              if (!ph.length) return null;
              return (
                <div key={a} className="mb-8" data-testid={`attendee-album-section-${a}`}>
                  <p className="overline mb-3">{a}</p>
                  {grid(ph)}
                </div>
              );
            })}
            {visible.some((p) => !p.album) && grid(visible.filter((p) => !p.album))}
            {hasMore && <div ref={sentinelRef} data-testid="gallery-sentinel" className="h-10" />}
          </>
        ) : (
          <>
            {grid(visible)}
            {hasMore && <div ref={sentinelRef} data-testid="gallery-sentinel" className="h-10" />}
          </>
        )}
        {lightbox && (
          <div className="fixed inset-0 z-50 bg-black/90 grid place-items-center p-6" onClick={() => setLightbox(null)}>
            <button className="absolute top-6 right-6 text-white/70 hover:text-white"><X size={26} /></button>
            <img src={photoFileUrl(lightbox.id)} alt="" className="max-h-[85vh] max-w-full rounded-lg object-contain" />
          </div>
        )}
      </div>
    </EventLayout>
  );
}
